import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Save, RotateCcw, Loader2, Info } from "lucide-react";

import { formatApiError } from "@/lib/api";
import { useDefault } from "@/lib/useDefault";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const TOGGLES = [
  {
    key: "require_photo_on_issue",
    label: "Require photo on Issue",
    help: "When a checklist row of kind 'OK / Issue' is marked Issue, the engineer must attach at least one photo tagged to that machine before submitting.",
  },
  {
    key: "require_notes_on_issue",
    label: "Require row notes on Issue",
    help: "When a checklist row of kind 'OK / Issue' is marked Issue, the row's inline note must be non-empty before submitting.",
  },
  {
    key: "auto_uncheck_on_start",
    label: "Auto-uncheck on Start",
    help: "When an engineer transitions a scheduled job to in-progress, clear every checklist answer so they start fresh. Idempotent — re-entering an already-in-progress job never clears state.",
  },
  {
    key: "default_photo_machine_tag_when_single",
    label: "Auto-tag photo to sole machine",
    help: "When a job has exactly one machine, photos uploaded without an explicit machine tag are automatically tagged to that machine.",
  },
];

// Server shape ⇄ form shape isolation lives in the page; transport is
// delegated to the shared useDefault hook.
const toForm = (d) => Object.fromEntries(TOGGLES.map((t) => [t.key, Boolean(d[t.key])]));

export default function ChecklistDefaults() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasPermission("settings.checklist.write");
  const canReset = hasPermission("settings.checklist.reset");

  const { value, loading, save, reset } = useDefault("checklist");
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (value) {
      const shape = toForm(value);
      setForm(shape);
      setBaseline(shape);
    }
  }, [value]);

  const dirty = baseline && form && JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    if (!canWrite || !form) return;
    setSaving(true);
    try {
      const data = await save(form);
      const shape = toForm(data);
      setForm(shape);
      setBaseline(shape);
      toast.success("Checklist defaults saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!canReset) return;
    if (!window.confirm("Reset checklist defaults to the seeded values? Unsaved changes will be lost.")) return;
    try {
      const data = await reset();
      const shape = toForm(data);
      setForm(shape);
      setBaseline(shape);
      toast.success("Defaults reset");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !form) {
    return <div className="p-6" data-testid="settings-checklist-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="p-6 space-y-6 pb-24" data-testid="settings-checklist-page">
      <PageHeader
        testId="settings-checklist-header"
        title="Checklist Defaults"
        subtitle="Global rules that shape the engineer's on-tablet checklist experience. Apply to every job."
        actions={
          <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-checklist-back">
            Back to Settings
          </Button>
        }
      />

      <Card className="rounded-md max-w-2xl">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-primary" /> Behaviour toggles
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          {TOGGLES.map((t) => (
            <div key={t.key} className="py-3 first:pt-0 last:pb-0 flex items-start justify-between gap-4" data-testid={`settings-checklist-row-${t.key}`}>
              <div className="min-w-0 space-y-1">
                <div className="text-sm font-medium">{t.label}</div>
                <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{t.help}</span>
                </div>
              </div>
              <Switch
                checked={form[t.key]}
                onCheckedChange={(v) => setForm((f) => ({ ...f, [t.key]: Boolean(v) }))}
                disabled={!canWrite}
                data-testid={`settings-checklist-toggle-${t.key}`}
                aria-label={t.label}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {canWrite && (
        <div className="fixed bottom-0 left-56 right-0 bg-background border-t border-border p-3 flex justify-between items-center z-30" data-testid="settings-checklist-actions-bar">
          <div className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "All saved"}
          </div>
          <div className="flex gap-2">
            {canReset && (
              <Button variant="outline" onClick={handleReset} data-testid="settings-checklist-reset-btn">
                <RotateCcw className="h-4 w-4" /> Reset to defaults
              </Button>
            )}
            <Button variant="outline" onClick={() => setForm(baseline)} disabled={!dirty} data-testid="settings-checklist-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!dirty || saving} data-testid="settings-checklist-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1">Save changes</span>
            </Button>
          </div>
        </div>
      )}

      {!canWrite && (
        <div className="text-xs text-muted-foreground italic" data-testid="settings-checklist-readonly-notice">
          You have read-only access to checklist defaults.
        </div>
      )}
    </div>
  );
}
